import { Button } from '../common/Button';
import { Avatar } from '../common/Avatar';

interface HomeHeaderProps {
  displayName: string;
  avatarUrl?: string;
  showPairingIndicator: boolean;
  onSettings: () => void;
  onPairing: () => void;
  onLogout: () => void;
}

/**
 * Sticky header for the home page.
 */
export function HomeHeader({
  displayName,
  avatarUrl,
  showPairingIndicator,
  onSettings,
  onPairing,
  onLogout,
}: HomeHeaderProps) {
  return (
    <header className="sticky top-0 z-40 bg-bg-primary/80 backdrop-blur-lg border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <BrandBlock />
          <HeaderActions
            displayName={displayName}
            avatarUrl={avatarUrl}
            showPairingIndicator={showPairingIndicator}
            onPairing={onPairing}
            onSettings={onSettings}
            onLogout={onLogout}
          />
        </div>
      </div>
    </header>
  );
}

interface HeaderActionsProps {
  displayName: string;
  avatarUrl?: string;
  showPairingIndicator: boolean;
  onPairing: () => void;
  onSettings: () => void;
  onLogout: () => void;
}

/**
 * Renders header action buttons.
 */
function HeaderActions({
  displayName,
  avatarUrl,
  showPairingIndicator,
  onPairing,
  onSettings,
  onLogout,
}: HeaderActionsProps) {
  return (
    <div className="flex items-center gap-4">
      {showPairingIndicator && <PairingIndicator onClick={onPairing} />}
      <SettingsButton onClick={onSettings} />
      <UserBadge displayName={displayName} avatarUrl={avatarUrl} />
      <Button variant="secondary" size="sm" onClick={onLogout}>
        Sign Out
      </Button>
    </div>
  );
}

/**
 * Renders the VR Meet brand mark.
 */
function BrandBlock() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      </div>
      <span className="text-lg font-semibold">VR Meet</span>
    </div>
  );
}

interface ActionButtonProps {
  onClick: () => void;
}

/**
 * Renders the settings button icon.
 */
function SettingsButton({ onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
      title="Settings"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    </button>
  );
}

/**
 * Renders the pairing indicator button.
 */
function PairingIndicator({ onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="p-2 text-amber-300 hover:text-amber-200 hover:bg-white/5 rounded-lg transition-colors"
      title="Pair a headset"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 12a6 6 0 016-6h4a6 6 0 016 6v2a3 3 0 01-3 3h-2a3 3 0 01-3-3v-1a1 1 0 00-1-1H9a1 1 0 00-1 1v1a3 3 0 01-3 3H4"
        />
      </svg>
    </button>
  );
}

interface UserBadgeProps {
  displayName: string;
  avatarUrl?: string;
}

/**
 * Renders the signed-in user badge.
 */
function UserBadge({ displayName, avatarUrl }: UserBadgeProps) {
  return (
    <div className="flex items-center gap-3">
      <Avatar name={displayName} imageUrl={avatarUrl} size="sm" />
      <span className="text-sm text-gray-300 hidden sm:block">{displayName}</span>
    </div>
  );
}
